import type { LucideProps } from 'lucide-react';
import {
  Church,
  Users,
  DollarSign,
  Dumbbell,
  Flame,
  Star,
  Target,
  Trophy,
  Crown,
  Pin,
  BookOpen,
  Activity,
  Heart,
  Sparkles,
  Sun,
  MessageCircle,
  BarChart3,
  CheckCircle,
  ClipboardList,
  TrendingUp,
  Zap,
  Award,
  Medal,
  Shield,
  Bot,
  KeyRound,
  Palette,
  Database,
  UserCog,
  Crosshair,
  Briefcase,
  Home,
  GraduationCap,
  CalendarCheck,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';

// Category icon mapping for the 4 Fs
export const CATEGORY_ICONS: Record<string, LucideIcon> = {
  faith: Church,
  family: Users,
  finance: DollarSign,
  fitness: Dumbbell,
};

// Category gradient colors
export const CATEGORY_GRADIENTS: Record<string, string> = {
  faith: 'from-amber-500 to-orange-500',
  family: 'from-rose-500 to-pink-500',
  finance: 'from-emerald-500 to-teal-500',
  fitness: 'from-blue-500 to-cyan-500',
};

// Category colors for badges/cards
export const CATEGORY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  faith: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400' },
  family: { bg: 'bg-rose-500/10', border: 'border-rose-500/30', text: 'text-rose-400' },
  finance: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' },
  fitness: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
};

// Admin tab icon mapping
export const ADMIN_TAB_ICONS: Record<string, LucideIcon> = {
  users: UserCog,
  ai: Bot,
  'api-keys': KeyRound,
  theme: Palette,
  branding: Sparkles,
  rag: BookOpen,
  database: Database,
  analytics: BarChart3,
};

// All available icons by name
const ICON_MAP: Record<string, LucideIcon> = {
  church: Church,
  users: Users,
  dollar: DollarSign,
  'dollar-sign': DollarSign,
  dumbbell: Dumbbell,
  flame: Flame,
  star: Star,
  target: Target,
  trophy: Trophy,
  crown: Crown,
  pin: Pin,
  book: BookOpen,
  activity: Activity,
  heart: Heart,
  sparkles: Sparkles,
  sun: Sun,
  chat: MessageCircle,
  'bar-chart': BarChart3,
  'check-circle': CheckCircle,
  'calendar-check': CalendarCheck,
  clipboard: ClipboardList,
  trending: TrendingUp,
  zap: Zap,
  award: Award,
  medal: Medal,
  shield: Shield,
  bot: Bot,
  key: KeyRound,
  palette: Palette,
  database: Database,
  'user-cog': UserCog,
  'user-plus': UserPlus,
  crosshair: Crosshair,
  briefcase: Briefcase,
  home: Home,
  graduation: GraduationCap,
};

// Habit icon options for the icon picker
export const HABIT_ICON_OPTIONS = [
  'pin', 'star', 'target', 'book', 'activity', 'dumbbell',
  'church', 'dollar', 'heart', 'sparkles', 'flame',
  'trophy', 'award', 'shield', 'zap', 'trending',
  'crown', 'medal', 'crosshair', 'briefcase', 'home', 'graduation',
] as const;

/**
 * Renders a Lucide icon by name string.
 * Handles backward compatibility with emoji strings from the database:
 * if the name contains non-ASCII characters (emoji), it renders as text.
 */
export function IconByName({ name, className, ...props }: { name: string } & Omit<LucideProps, 'ref'>) {
  // Check if the name is an emoji (non-ASCII)
  if (/[^\x00-\x7F]/.test(name)) {
    return <span className={className}>{name}</span>;
  }

  const IconComponent = ICON_MAP[name];
  if (!IconComponent) {
    return <Pin className={className} {...props} />;
  }
  return <IconComponent className={className} {...props} />;
}

export {
  Church, Users, DollarSign, Dumbbell, Flame, Star, Target, Trophy, Crown,
  Pin, BookOpen, Activity, Heart, Sparkles, Sun, MessageCircle, BarChart3,
  CheckCircle, ClipboardList, TrendingUp, Zap, Award, Medal, Shield,
  Bot, KeyRound, Palette, Database, UserCog, CalendarCheck, UserPlus,
};
